import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Hook that merges OWL-Bench device state with Lightbug live data
 * (including GPS from latestPoints) to produce map-ready device objects.
 * Only shows real devices from st.devices.
 */
export function useMapDevices(st, lightbugLive) {
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);

  // Build location → client lookup from st.locations
  const locClientMap = useMemo(() => {
    const map = {};
    (st.locations || []).forEach(l => {
      const name = l.name || '';
      if (name) map[name] = l.customer || '';
    });
    return map;
  }, [st.locations]);

  // Mark loading done once lightbugLive populates
  useEffect(() => {
    if (loading && lightbugLive && Object.keys(lightbugLive).length > 0) {
      setLoading(false);
      setLastSync(new Date());
    }
  }, [lightbugLive, loading]);

  // Also stop loading after a timeout (API may not be available)
  useEffect(() => {
    const t = setTimeout(() => { setLoading(false); }, 3000);
    return () => clearTimeout(t);
  }, []);

  // Build ALL devices for the list panel (with or without GPS)
  const allDevices = useMemo(() => {
    const stDevices = st.devices || [];

    return stDevices.map(d => {
      const lb = d.deviceId ? (lightbugLive?.[String(d.deviceId)] || {}) : {};
      const loc = d.location || '';
      const client = locClientMap[loc] || '';

      // Determine online/idle/offline status from Lightbug live data
      let mapStatus = 'offline';
      if (lb.lastSeen) {
        const seenAgo = Date.now() - new Date(lb.lastSeen).getTime();
        if (seenAgo < 30 * 60 * 1000) mapStatus = 'online';        // < 30 min
        else if (seenAgo < 24 * 60 * 60 * 1000) mapStatus = 'idle'; // < 24 hr
      }

      return {
        id: d.id,
        serial: d.serial || '',
        deviceId: d.deviceId || '',
        name: d.serial || `Device ${d.id}`,
        type: d.type || 'RH2',
        client,
        status: mapStatus,
        lat: lb.lat ?? null,
        lng: lb.lng ?? null,
        hasGps: lb.lat != null && lb.lng != null,
        battery: lb.batteryPct ?? null,
        accuracy: lb.accuracy ?? null,
        fix: lb.fixType || null,
        lastSeen: lb.lastSeen ? formatRelativeTime(lb.lastSeen) : 'Unknown',
        gpsAddress: lb.gpsAddress || null,
        location: loc,
        importBatch: (d.importBatch || '').trim(),
        firmware: lb.firmware || d.firmware || '',
        owlStatus: d.status,
      };
    });
  }, [st.devices, lightbugLive, locClientMap]);

  // Devices with GPS — for the map markers
  const mappableDevices = useMemo(
    () => allDevices.filter(d => d.hasGps),
    [allDevices]
  );

  const counts = useMemo(() => ({
    total: allDevices.length,
    online: allDevices.filter(d => d.status === 'online').length,
    idle: allDevices.filter(d => d.status === 'idle').length,
    offline: allDevices.filter(d => d.status === 'offline').length,
  }), [allDevices]);

  const refetch = useCallback(() => {
    setLastSync(new Date());
  }, []);

  return { devices: allDevices, mappableDevices, loading, refetch, counts, lastSync };
}

function formatRelativeTime(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}
